import { useEffect, useState } from "react"
import { useRouter } from "next/router"
import { Box, useBreakpointValue, Tabs } from "@chakra-ui/react"
import Wrapper from "components/layout/wrapper"
import dynamic from "next/dynamic"
import { useGlobalState } from "providers/globalState"
import { BlockStateProvider } from "providers/blockState"
import { scrollTo } from "src/util/helpers"

const Body = dynamic(() => import("components/matrix/body"))
const TwoColumn = dynamic(() => import("components/matrix/twoColumn"))
const AccordionComponent = dynamic(() => import("components/matrix/accordion"))
const AgendaSession = dynamic(() => import("components/matrix/agendaSession"))
const AdvancedAgenda = dynamic(() => import("components/matrix/advancedAgenda"))
const FacultyMatrix = dynamic(() => import("components/matrix/facultyMatrix"))
const Row = dynamic(() => import("components/matrix/row"))

export default function TabsComponent({ block, colorMode, padTop, padBottom, margin = null }) {
    const router = useRouter()
    const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md" })
    const { site } = useGlobalState()
    const [currentTabIndex, setcurrentTabIndex] = useState("tab-0")

    // determine background
    let bg = block.backgroundColor ? block.backgroundColor : ""
    const bgImage = block.backgroundImage ? block.backgroundImage[0] : null
    bg += bgImage ? " url(" + bgImage.url + ")" : ""

    // open specific tab if query param is present, 0-based
    useEffect(() => {
        if (!router.query.showtab || !block.identifier) return

        const lastDashIndex = router.query.showtab.lastIndexOf("-")

        const tabsId = router.query.showtab.slice(0, lastDashIndex)
        const tabId = parseInt(router.query.showtab.slice(lastDashIndex + 1), 10)

        if (tabsId !== block.identifier) return

        if (tabId < block.tabs.length) {
            setcurrentTabIndex(`tab-${tabId}`)
        }

        // if we have a hash in the URL, scroll to it
        if (window.location.hash) {
            setTimeout(() => {
                const element = document.getElementById(window.location.hash.slice(1))
                if (element) {
                    scrollTo(element)
                }
            }, 200)
        }
        // otherwise, scroll to the tabs block
        else {
            setTimeout(() => {
                const element = document.getElementById(block.identifier)
                if (element) {
                    scrollTo(element)
                }
            }, 200)
        }
    }, [router.query])

    function renderSwitch(block) {
        switch (block.type) {
            case "body": {
                return (
                    <Box className="body">
                        <Body block={block} colorMode="dark" />
                    </Box>
                )
            }
            case "twoColumn": {
                return (
                    <Box className="body">
                        <TwoColumn block={block} colorMode="dark" />
                    </Box>
                )
            }
            case "accordion": {
                return (
                    <Box className="body">
                        <AccordionComponent block={block} colorMode="dark" />
                    </Box>
                )
            }
            case "agendaSession": {
                return <AgendaSession block={block} meetingId={site.meeting.emmaMeetingId} channels={site.channels} />
            }
            case "advancedAgenda": {
                return <AdvancedAgenda block={block} site={site} />
            }
            case "faculty": {
                return <FacultyMatrix block={block} colorMode={colorMode} meetingId={site.meeting.emmaMeetingId} />
            }
            case "row": {
                return <Row block={block} colorMode={colorMode} />
            }
        }
    }

    return (
        <Wrapper className={"tabs-matrix colorMode--light"} id={block.identifier} bg={bg} bgPosition="bottom" py="20px">
            <Box className={"chakra-tabs-parent"} bgColor="#fff" boxShadow={"3px 3px 3px rgba(0,0,0,.3)"}>
                <Tabs.Root size="lg" value={currentTabIndex} onValueChange={tab => setcurrentTabIndex(tab.value)}>
                    <Tabs.List
                        borderColor="color5"
                        borderBottomWidth="1px"
                        justifyContent={block.tabs.length < 3 ? "start" : "space-between"}
                        gap={block.tabs.length < 4 ? ["0px", "30px"] : "0px"}
                        flexDirection={isMobile ? "column" : "row"}
                    >
                        {block.tabs.map((tab, i) => {
                            const clr = tab.labelColor === "main" ? "color5" : "#888"
                            return (
                                <Tabs.Trigger
                                    key={i}
                                    value={`tab-${i}`}
                                    className={"__tab_button"}
                                    height={isMobile ? null : "unset"}
                                    fontWeight={600}
                                    fontSize="18px"
                                    color={clr}
                                    borderBottomWidth="4px"
                                    justifyContent="left"
                                    mb={0}
                                    pl={0}
                                    pr={0}
                                    pb={isMobile ? 1 : 3}
                                    _selected={{
                                        color: clr,
                                        borderColor: clr,
                                    }}
                                    _active={{
                                        color: clr,
                                        bgColor: "transparent",
                                        className: "active",
                                    }}
                                >
                                    <Box
                                        as={"span"}
                                        dangerouslySetInnerHTML={{
                                            __html: tab.label,
                                        }}
                                    ></Box>
                                    <Box className={"activeBox"}></Box>
                                </Tabs.Trigger>
                            )
                        })}
                    </Tabs.List>

                    {block.tabs.map((tab, i) => (
                        <Tabs.Content key={i} p={0} value={`tab-${i}`}>
                            {tab.page[0].matrix.map((tabPageBlock, ii) => (
                                <BlockStateProvider key={ii} block={tabPageBlock}>
                                    {renderSwitch(tabPageBlock)}
                                </BlockStateProvider>
                            ))}
                        </Tabs.Content>
                    ))}
                </Tabs.Root>
            </Box>
        </Wrapper>
    )
}
